import { Injectable, inject } from '@angular/core';
import {
  BoelsBaseSite,
  BOProduct,
  ProductReference,
  RedirectModel,
} from '@boels/models';
import { combineLatest, filter, Observable, switchMap } from 'rxjs';
import { HttpService, UrlService } from '@boels/helpers';
import { SiteContextFacade } from '../../site-context/facade/site-context.facade';
import { CultureFacade } from '../../cultures/facade/cultures.facade';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'any',
})
export class ProductGatewayService {
  private readonly httpService = inject(HttpService);
  private urlService = inject(UrlService);
  private siteContextFacade = inject(SiteContextFacade);
  private cultureFacade = inject(CultureFacade);

  getProduct(code: string): Observable<BOProduct> {
    return <Observable<BOProduct>>combineLatest([
      this.siteContextFacade.getActiveBaseSiteEntity().pipe(filter(Boolean)),
      this.cultureFacade.getActiveLanguage(),
      this.cultureFacade.getActiveCurrency(),
    ]).pipe(
      switchMap(([activeBaseSite, lang, currency]) => {
        const params = [
          ['lang', lang],
          ['curr', currency],
          ['fields', 'FULL'],
        ];

        const url = this.urlService.combineUri(
          activeBaseSite.uid,
          'products',
          code,
        );
        return this.httpService.get(url, params);
      }),
    );
  }

  getProductReferences(
    code: string,
    referenceType?: string,
    pageSize?: number,
  ): Observable<ProductReference[]> {
    return <Observable<ProductReference[]>>combineLatest([
      this.siteContextFacade.getActiveBaseSiteEntity(),
      this.cultureFacade.getActiveLanguage(),
      this.cultureFacade.getActiveCurrency(),
    ]).pipe(
      switchMap(([activeBaseSite, lang, currency]) => {
        const params = [
          ['lang', lang],
          ['curr', currency],
          ['fields', 'DEFAULT,references(target(images(FULL)))'],
        ];

        if (referenceType) params.push(['referenceType', referenceType]);
        if (pageSize) params.push(['pageSize', pageSize.toString()]);

        const url = this.urlService.combineUri(
          activeBaseSite.uid,
          'products',
          code,
          'references',
        );
        return this.httpService.get<{ references: ProductReference[] }>(
          url,
          params,
        );
      }),
      map((response) => response?.references),
    );
  }

  getRedirectForProduct(productCode: string): Observable<RedirectModel> {
    return combineLatest([
      this.siteContextFacade.getActiveBaseSiteEntity(),
      this.cultureFacade.getActiveLanguage(),
      this.cultureFacade.getActiveCurrency(),
    ]).pipe(
      filter(
        ([baseSite, lang, curr]: [BoelsBaseSite, string, string]) =>
          !!baseSite && !!lang && !!curr,
      ),
      switchMap(([activeBaseSite, lang, currency]) => {
        const params = [
          ['fields', 'FULL'],
          ['lang', lang],
          ['curr', currency],
          ['productCode', productCode],
        ];

        return this.httpService.get<RedirectModel>(
          `${activeBaseSite.uid}/product-redirects`,
          params,
        );
      }),
    );
  }
}
